"use client";

// UIArc — source MIT (c) 2026 Elia Kuratli, arc-library@7238f981 registry/components/line-chart/line-chart.tsx.
// Import fidèle (ui/components) : imports canoniques Foundry, jetons --arc-* (outil U1 arc_tokens). Voir UI-BANK.md.
import { useCallback, useEffect, useId, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore, type CSSProperties, type FocusEvent, type KeyboardEvent, type PointerEvent, type Ref, type RefObject } from "react";
import { AnimatePresence, animate, isMotionValue, motion, useInView, useMotionValue, useMotionValueEvent, useReducedMotionConfig as useReducedMotion, useSpring, useTransform, type MotionValue } from "motion/react";
import { motionTokens } from "../../lib/motion-tokens";
import styles from "./line-chart.module.css";

export interface LineChartSeries {
  /** Stable identity. A series keeps its line across data changes, so switching ranges morphs the path instead of redrawing it. */
  key: string;
  /** Name in the legend, the tooltip, and the data table. */
  label: string;
  /** Any CSS color. Defaults to the accent for the first series, then quieter neutral steps. */
  color?: string;
  /** Draws a dashed stroke, useful for a previous period or a target. */
  dashed?: boolean;
  /** Fills the area under the line with a faint tint of its color. Defaults to true for the first series. */
  area?: boolean;
  /**
   * Foundry, promotion additive (Finance8, 05/10/2026). With `stack="signed"`, keeps this series out of the stack: it is drawn
   * from its own values, such as a comparison or a target line. Ignored without a stack.
   */
  overlay?: boolean;
  /**
   * Foundry, promotion additive (Analytics15, 06/10/2026). With `progress`, draws this series in full instead of revealing it up to
   * the playhead, such as a quiet comparison line under a replay. Ignored without `progress`.
   */
  whole?: boolean;
}

/**
 * Foundry, promotion additive (Finance8, 05/10/2026). A labelled horizontal rule, such as a budget or an alert threshold. Its value
 * is part of the value domain, so a rule above the data is never drawn off the plot. A rule that moves glides to its new height.
 */
export interface LineChartRule {
  /** Stable identity: a rule whose key survives a change glides instead of being replaced. */
  key: string;
  value: number;
  /** Text printed above the rule and read in the summary, already formatted, such as "Budget €2,000". */
  label: string;
  tone?: "neutral" | "accent" | "warning" | "danger";
  dashed?: boolean;
  /**
   * Foundry, promotion additive (Analytics15). Makes the rule a handle people drag or move with the keyboard, such as a budget.
   * The value stays controlled: the chart reports `onChange` while it moves and `onCommit` once on release or per key press.
   * A draggable rule does not widen the value domain; pass `domain` so its whole range stays on the plot.
   */
  drag?: LineChartRuleDrag;
}

/** Foundry, promotion additive (Analytics15). The range, step and callbacks of a draggable rule. */
export interface LineChartRuleDrag {
  min: number;
  max: number;
  step: number;
  /** Page up and Page down move this far. Defaults to five steps. */
  largeStep?: number;
  /** Accessible name of the handle, such as "Monthly budget". */
  label: string;
  /** Value text read by assistive technology, such as "€2,000, crossed on Sep 24". Defaults to the rule label. */
  valueText?: (value: number) => string;
  onChange: (value: number) => void;
  onCommit?: (value: number) => void;
  disabled?: boolean;
}

/**
 * Foundry, promotion additive (Analytics15). A shaded band between two series of the same chart, such as a forecast cone between
 * its low and high estimate. Drawn only where both series have a value.
 */
export interface LineChartBand {
  key: string;
  /** Key of the series that bounds the band from below. */
  lower: string;
  /** Key of the series that bounds the band from above. */
  upper: string;
  tone?: "accent" | "neutral" | "warning";
}

/**
 * Foundry, promotion additive (Analytics15). A dot that settles onto a series at a position on the time axis, such as an annotated
 * moment. `at` is a fractional index into `data`, so a moment between two points sits on the curve between them.
 */
export interface LineChartMarker {
  key: string;
  series: string;
  at: number;
  /** A hidden marker keeps its place and fades out; a shown one settles onto the line. */
  shown: boolean;
  tone?: "neutral" | "accent" | "warning" | "danger";
}

export interface LineChartDatum {
  /** Stable identity, such as an ISO date. */
  key: string;
  /** Full label for the tooltip and screen readers, such as "Tue, Sep 15". */
  label: string;
  /** Short label on the x axis, such as "Sep 15". Leave it out to keep the axis quiet at that point. */
  axisLabel?: string;
  /** One value per series key. A missing value counts as zero, unless `missing="gap"` leaves a gap in that series. */
  values: Record<string, number | undefined>;
  /**
   * Foundry, promotion additive (Analytics15 F-R2, 06/10/2026). Real position on the time axis, such as minutes from the start. When
   * every datum has one, points sit at their true intervals (curve, crosshair, markers, labels and `progress` alike); leave it out
   * everywhere for evenly spaced points. Given on only some points, or not strictly increasing, it throws.
   */
  x?: number;
}

/** Use a line chart for one or more measures over time, when the shape of the trend matters more than any single value. */
export interface LineChartProps {
  data: LineChartDatum[];
  series: LineChartSeries[];
  /** What is measured, such as "Signups". Names the chart for assistive technology. */
  label: string;
  /** Unit after each value in the tooltip, such as "ms". */
  unit?: string;
  /** Plot height in pixels. The width follows the container. */
  height?: number;
  /** Formats values in the tooltip and data table. */
  formatValue?: (value: number, series: LineChartSeries) => string;
  /** Formats the value axis. Defaults to a compact number, such as 1.2K. */
  formatTick?: (value: number) => string;
  /** Controlled hidden series keys. */
  hiddenSeries?: string[];
  defaultHiddenSeries?: string[];
  onHiddenSeriesChange?: (hidden: string[]) => void;
  /** Called as the crosshair moves, and with null when it leaves. Use it to drive a headline readout. */
  onActiveChange?: (index: number | null, datum: LineChartDatum | null) => void;
  /** Keeps the current lines on screen, dimmed, while the next range loads. */
  loading?: boolean;
  /** Shown when there are no points. */
  emptyLabel?: string;
  /** Series toggles above the plot. Shown by default when there is more than one series. */
  legend?: boolean;
  /** Header of the first column in the data table read by screen readers. */
  categoryLabel?: string;
  /** Smooth monotone curves that never swing past the data, or straight segments. */
  curve?: "smooth" | "linear";
  /** Foundry, promotion additive (Finance8). Labelled horizontal rules, included in the value domain. */
  rules?: LineChartRule[];
  /**
   * Foundry, promotion additive (Finance8). `signed` stacks the series as bands: positive values pile up from zero and negative
   * values pile down from zero, so a negative measure (churn) keeps its real sign. Series marked `overlay` stay unstacked.
   * The tooltip and the data table read each series' own value, never the running total. Defaults to `none`.
   */
  stack?: "none" | "signed";
  /**
   * Foundry, promotion additive (Finance8). `zero` (default) reads a missing value as zero. `gap` leaves the series undrawn where
   * a value is missing: a projection can start exactly where the observed line ends, and absent data is never drawn as zero.
   */
  missing?: "zero" | "gap";
  /**
   * Foundry, promotion additive (Analytics15). Controlled crosshair: the index on show, or null for none. Gestures report the index
   * they ask for through `onActiveChange`; the chart shows only what this prop says. Leave it out to keep the crosshair internal.
   */
  active?: number | null;
  /**
   * Foundry, promotion additive (Analytics15). An explicit value domain instead of one fitted to the visible series, so several
   * charts can share one scale or each fit their own. A new domain springs the axis and the lines together.
   */
  domain?: { min: number; max: number };
  /**
   * Foundry, promotion additive (Analytics15). Reveals the series up to this share of the time axis (0 to 1) with a playhead, such
   * as a replay. Pass a motion value to drive it every frame without rendering React; series marked `whole` stay drawn in full.
   */
  progress?: number | MotionValue<number>;
  /** Foundry, promotion additive (Analytics15). Text riding the tip of the first revealed series, rewritten only when it changes. */
  progressLabel?: (at: number) => string;
  /** Foundry, promotion additive (Analytics15). A clock written on the time axis under the playhead; axis labels fade out of its way. */
  progressAxisLabel?: (at: number) => string;
  /** Foundry, promotion additive (Analytics15). Shaded bands between two series, such as a forecast cone. */
  bands?: LineChartBand[];
  /** Foundry, promotion additive (Analytics15). Dots that settle onto a series, such as replay moments. */
  markers?: LineChartMarker[];
  /**
   * Foundry, promotion additive (Analytics15). `false` leaves the plot out of the tab order and ignores pointers, for a chart whose
   * crosshair is driven from outside through `active` (one control for several charts). Defaults to true.
   */
  interactive?: boolean;
  /** Foundry, promotion additive (Analytics15). `false` hides the floating tooltip while the crosshair is shown. Defaults to true. */
  tooltip?: boolean;
  /**
   * Foundry, promotion additive (Analytics15). `false` draws the line alone (no gridlines, value gutter or date axis), as a sparkline
   * inside a card or a list row that already names and states the value; ranges still morph. Defaults to true.
   */
  axes?: boolean;
  ref?: Ref<HTMLElement>;
  className?: string;
}

type Point = [x: number, value: number];
/** `base` is the lower edge of a stacked band, sampled on the same x positions as `shape`; null outside a stack. */
type Track = { shape: Point[]; base: Point[] | null; presence: number; fade: { stop: () => void } | null };
type Target = { top: Point[]; base: Point[] | null };

const { spring, duration, ease } = motionTokens;
/** Motion drops inherited velocity on time-defined springs, so values that retarget mid-flight run the same springs written as stiffness and damping. */
const physical = ({ visualDuration, bounce }: { visualDuration: number; bounce: number }, restDelta = .001) => { const root = (2 * Math.PI) / (visualDuration * 1.2); return { type: "spring" as const, stiffness: root * root, damping: 2 * (1 - bounce) * root, restDelta, restSpeed: restDelta * 2 }; };
const settle = physical(spring.smooth);
const glide = physical(spring.snappy, .0001);
const follow = { stiffness: glide.stiffness, damping: glide.damping, restDelta: .01 };
const draw = { duration: duration.considered * 1.75, ease: [...ease.inOut] } as const;
const fadeFast = { duration: duration.instant, ease: [...ease.standard] } as const;

/** Room above the top gridline, and the gap between the crosshair and its tooltip. */
const TOP = 12, GAP = 14;
const PALETTE = ["var(--arc-accent)", "color-mix(in oklab, var(--arc-foreground) 46%, var(--arc-surface))", "color-mix(in oklab, var(--arc-foreground) 26%, var(--arc-surface))"];
const grouped = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 1 });
const compact = new Intl.NumberFormat("fr-FR", { notation: "compact", maximumFractionDigits: 1 });
const clamp = (value: number, low: number, high: number) => Math.min(high, Math.max(low, value));
const colorOf = (series: LineChartSeries, index: number) => series.color ?? PALETTE[Math.min(index, PALETTE.length - 1)];
const clean = (value: number) => Number(value.toPrecision(12));

/**
 * Foundry (strict, 05/10/2026) — lecture bornée d'un index INTERNE (valeurs, pentes, tangentes, échantillons, couples de morph).
 * Un index absent est une violation d'invariant, signalée explicitement, jamais une fausse mesure.
 */
function itemAt<T>(list: ArrayLike<T>, index: number): T {
  const value = list[index];
  if (value === undefined) throw new RangeError(`line-chart: index ${index} outside [0, ${list.length})`);
  return value;
}

/** Clean gridlines: the smallest step of 1, 2, 2.5, or 5 that covers the data in four rows or fewer, from zero unless the data dips below it. */
function niceScale(low: number, high: number) {
  const lo = Math.min(0, low), hi = Math.max(high, lo + 1);
  const span = hi - lo, magnitude = 10 ** Math.floor(Math.log10(span / 4));
  for (const factor of [1, 2, 2.5, 5, 10]) {
    const step = factor * magnitude, bottom = Math.floor(lo / step) * step, top = Math.ceil(hi / step) * step;
    if ((top - bottom) / step <= 4 + 1e-9) {
      const ticks: number[] = [];
      for (let value = bottom; value <= top + step / 2; value += step) ticks.push(clean(value));
      return { min: clean(bottom), max: clean(top), ticks };
    }
  }
  return { min: lo, max: hi, ticks: [lo, hi] };
}

/**
 * Foundry (Analytics15) — un domaine EXPLICITE garde ses bornes exactes ; seules les graduations sont « propres » (pas de 1, 2, 2.5
 * ou 5 en quatre rangées au plus) à l'intérieur. Un domaine vide ou inversé est refusé par l'appelant (repli sur l'ajustement).
 */
function scaleWithin(min: number, max: number) {
  const span = max - min, magnitude = 10 ** Math.floor(Math.log10(span / 4));
  for (const factor of [1, 2, 2.5, 5, 10]) {
    const step = factor * magnitude;
    if (span / step <= 4 + 1e-9) {
      const ticks: number[] = [];
      for (let value = Math.ceil(min / step - 1e-9) * step; value <= max + step * 1e-6; value += step) ticks.push(clean(value));
      return { min: clean(min), max: clean(max), ticks };
    }
  }
  return { min, max, ticks: [min, max] };
}

/** The line through every value, sampled densely on x from 0 to 1 in data units. Monotone cubic tangents keep a smooth curve from swinging past the data. */
function curveFor(values: number[], smooth: boolean): Point[] {
  if (values.length === 0) return [];
  if (values.length === 1) { const only = itemAt(values, 0); return [[0, only], [1, only]]; }
  const last = values.length - 1, step = 1 / last;
  if (!smooth) return values.map((value, index) => [index * step, value]);
  const slopes = values.slice(1).map((value, index) => (value - itemAt(values, index)) / step);
  // Tangentes intérieures : pentes voisines index-1 et index, toutes deux dans [0, last-1] pour 0 < index < last.
  const tangents = values.map((_, index) => {
    if (index === 0 || index === last) return 0;
    const before = itemAt(slopes, index - 1), after = itemAt(slopes, index);
    return (Math.sign(before) + Math.sign(after)) * Math.min(Math.abs(before), Math.abs(after), Math.abs(before + after) / 4) || 0;
  });
  if (last > 1) { tangents[0] = (3 * itemAt(slopes, 0) - itemAt(tangents, 1)) / 2; tangents[last] = (3 * itemAt(slopes, last - 1) - itemAt(tangents, last - 1)) / 2; }
  else { const s0 = itemAt(slopes, 0); tangents[0] = s0; tangents[1] = s0; }
  const samples = Math.max(1, Math.min(16, Math.round(192 / last)));
  const points: Point[] = [];
  for (let index = 0; index < last; index++) {
    const v0 = itemAt(values, index), v1 = itemAt(values, index + 1), m0 = itemAt(tangents, index), m1 = itemAt(tangents, index + 1);
    const low = Math.min(v0, v1), high = Math.max(v0, v1);
    for (let sample = 0; sample < samples; sample++) {
      const t = sample / samples, t2 = t * t, t3 = t2 * t;
      const value = (2 * t3 - 3 * t2 + 1) * v0 + (t3 - 2 * t2 + t) * step * m0 + (3 * t2 - 2 * t3) * v1 + (t3 - t2) * step * m1;
      points.push([(index + t) * step, clamp(value, low, high)]);
    }
  }
  points.push([1, itemAt(values, last)]);
  return points;
}

/** Value of a sampled line at x, straight between samples. */
function valueAt(shape: Point[], x: number) {
  if (!shape.length) return 0;
  let low = 0, high = shape.length - 1;
  const head = itemAt(shape, low), tail = itemAt(shape, high);
  if (x <= head[0]) return head[1];
  if (x >= tail[0]) return tail[1];
  // Recherche binaire : low < middle < high reste dans la forme.
  while (high - low > 1) { const middle = (low + high) >> 1; if (itemAt(shape, middle)[0] < x) low = middle; else high = middle; }
  const [x0, y0] = itemAt(shape, low), [x1, y1] = itemAt(shape, high);
  return x1 === x0 ? y1 : y0 + ((y1 - y0) * (x - x0)) / (x1 - x0);
}

/**
 * Foundry (Analytics15 F-R2) — axe réel. `null` : axe ordinal historique (point i à i / dernier). Sinon, position [0, 1] de chaque
 * point d'après son `x`. Absent partout = ordinal ; présent sur une partie seulement, non fini ou non strictement croissant = erreur
 * explicite (une configuration illisible n'est jamais rabattue en silence sur l'ordinal).
 */
export function axisPositions(data: readonly LineChartDatum[]): number[] | null {
  const given = data.filter(item => item.x !== undefined).length;
  if (given === 0) return null;
  if (given !== data.length) throw new RangeError(`line-chart: x given on ${given} of ${data.length} points; give it on every point or none`);
  if (data.length < 2) return null;
  const xs = data.map(item => item.x as number);
  for (let i = 0; i < xs.length; i++) {
    if (!Number.isFinite(xs[i]!)) throw new RangeError(`line-chart: x at point ${i} is not finite`);
    if (i > 0 && !(xs[i]! > xs[i - 1]!)) throw new RangeError(`line-chart: x must strictly increase (point ${i})`);
  }
  const first = xs[0]!, span = xs[xs.length - 1]! - first;
  return xs.map(x => (x - first) / span);
}

/** Position ordinale u ∈ [0, 1] → position sur l'axe réel, linéaire entre deux points voisins (mêmes points, mêmes valeurs). */
function toAxis(axis: readonly number[], u: number) {
  const last = axis.length - 1, f = clamp(u, 0, 1) * last, i = Math.min(last - 1, Math.floor(f)), a = itemAt(axis, i);
  return a + (itemAt(axis, i + 1) - a) * (f - i);
}

/** Inverse : position sur l'axe réel → index fractionnaire de point. */
function axisToIndex(axis: readonly number[], p: number) {
  const last = axis.length - 1, x = clamp(p, 0, 1);
  let i = 0;
  while (i < last - 1 && itemAt(axis, i + 1) < x) i++;
  const a = itemAt(axis, i), b = itemAt(axis, i + 1);
  return i + (b > a ? (x - a) / (b - a) : 0);
}

/**
 * Foundry (Finance8) — une série à trous : chaque suite de valeurs définies devient sa propre courbe, posée à sa place sur l'axe ;
 * un point `NaN` à mi-chemin de chaque trou sépare les suites. L'interpolation d'un morph traverse ce point en `NaN`, donc un trou
 * reste un trou pendant le mouvement. Sans aucun trou, la courbe est exactement celle de `curveFor`.
 */
function curveWithGaps(values: (number | undefined)[], smooth: boolean): Point[] {
  if (values.every(value => value !== undefined)) return curveFor(values as number[], smooth);
  const last = values.length - 1;
  const runs: [number, number][] = [];
  values.forEach((value, index) => {
    if (value === undefined) return;
    const run = runs[runs.length - 1];
    if (run && run[1] === index - 1) run[1] = index; else runs.push([index, index]);
  });
  const points: Point[] = [];
  runs.forEach(([from, to], rank) => {
    const sub = values.slice(from, to + 1) as number[];
    const span = to - from;
    // Une suite d'un seul point garde sa place (trait nul à bouts ronds) au lieu de s'étirer sur toute la largeur.
    const shape = span === 0 ? [[0, itemAt(sub, 0)] as Point] : curveFor(sub, smooth);
    for (const [x, value] of shape) points.push([last > 0 ? (from + x * span) / last : 0, value]);
    const next = runs[rank + 1];
    if (next && last > 0) points.push([(to + next[0]) / 2 / last, Number.NaN]);
  });
  return points;
}

/** Foundry (Finance8) — pile signée : par point, chaque série empile sur le cumul positif ou négatif de son signe. */
function stackSigned(data: LineChartDatum[], series: LineChartSeries[], hidden: string[], read: (datum: LineChartDatum, key: string) => number | undefined) {
  const out = new Map<string, { top: (number | undefined)[]; base: (number | undefined)[] | null }>();
  for (const line of series) out.set(line.key, { top: [], base: line.overlay ? null : [] });
  for (const datum of data) {
    let positive = 0, negative = 0;
    for (const line of series) {
      const slot = out.get(line.key)!, raw = read(datum, line.key);
      if (line.overlay) { slot.top.push(raw); continue; }
      if (raw === undefined) { slot.top.push(undefined); slot.base?.push(undefined); continue; }
      const value = hidden.includes(line.key) ? 0 : raw;
      if (raw >= 0) { slot.base?.push(positive); positive += value; slot.top.push(positive); }
      else { slot.base?.push(negative); negative += value; slot.top.push(negative); }
    }
  }
  return out;
}

/** Foundry (Finance8) — découpe une forme en segments continus (un `NaN` lève la plume). */
function segmentsOf(shape: Point[]): Point[][] {
  const segments: Point[][] = [];
  let current: Point[] = [];
  for (const point of shape) {
    if (Number.isFinite(point[1])) current.push(point);
    else if (current.length) { segments.push(current); current = []; }
  }
  if (current.length) segments.push(current);
  return segments;
}

/** Tracks are mutable animation state read by the painter, never by render. */
const update = (track: Track, patch: Partial<Track>) => Object.assign(track, patch);

const grid = Array.from({ length: 241 }, (_, index) => index / 240);

const subscribeNothing = () => () => {};
/** Reduced motion is only known in the browser, so the first client render matches the server before it takes effect. */
function useReducedMotionSafe() {
  const hydrated = useSyncExternalStore(subscribeNothing, () => true, () => false);
  return !!useReducedMotion() && hydrated;
}

type Scale = { min: MotionValue<number>; max: MotionValue<number> };
function useRowY(value: number, scale: Scale, height: number) {
  return useTransform(() => { const span = scale.max.get() - scale.min.get() || 1; return Math.round(TOP + (1 - (value - scale.min.get()) / span) * (height - TOP)) + .5; });
}

/** A gridline rides the scale, so a taller range slides it down while new rows fade in above. */
function Gridline({ value, scale, height, zero = false }: { value: number; scale: Scale; height: number; zero?: boolean }) {
  const y = useRowY(value, scale, height);
  return <motion.line className={styles.grid} data-zero={zero || undefined} x1={0} x2="100%" y1={y} y2={y} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, transition: fadeFast }} transition={{ duration: duration.standard, ease: [...ease.standard] }} />;
}

function TickLabel({ value, scale, height, format }: { value: number; scale: Scale; height: number; format: (value: number) => string }) {
  const y = useRowY(value, scale, height);
  return <motion.span className={styles.tick} style={{ y }} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, transition: fadeFast }} transition={{ duration: duration.standard, ease: [...ease.standard] }}>
    <span className={styles.tickText}>{format(value)}</span>
  </motion.span>;
}

/**
 * Foundry (Finance8) — une règle horizontale : sa valeur glisse sur le ressort du graphe quand elle change, et elle apparaît une fois
 * la ligne tracée (son opacité suit le tracé), comme dans la référence publique. Peinture : trait SVG + libellé HTML au-dessus.
 */
function useRuleY(rule: LineChartRule, scale: Scale, height: number, reduced: boolean, following = false) {
  const value = useMotionValue(rule.value);
  useEffect(() => {
    if (value.get() === rule.value) return;
    // Foundry (Analytics15) : une règle tirée suit le pointeur un pour un ; relâchée ou déplacée au clavier, elle se pose sur le ressort.
    if (reduced || following) { value.jump(rule.value); return; }
    const controls = animate(value, rule.value, rule.drag ? glide : settle);
    return () => controls.stop();
  }, [following, reduced, rule.drag, rule.value, value]);
  return useTransform(() => { const span = scale.max.get() - scale.min.get() || 1; return Math.round(TOP + (1 - (value.get() - scale.min.get()) / span) * (height - TOP)) + .5; });
}

function RuleLine({ rule, scale, height, reduced, drawn, following }: { rule: LineChartRule; scale: Scale; height: number; reduced: boolean; drawn: MotionValue<number>; following: boolean }) {
  const y = useRuleY(rule, scale, height, reduced, following);
  const opacity = useTransform(drawn, [.55, 1], [0, 1]);
  return <motion.line className={styles.rule} data-tone={rule.tone ?? "neutral"} data-dashed={rule.dashed || undefined} data-rule={rule.key} data-draggable={rule.drag ? true : undefined} x1={0} x2="100%" y1={y} y2={y} style={{ opacity }} />;
}

function RuleLabel({ rule, scale, height, reduced, drawn, following }: { rule: LineChartRule; scale: Scale; height: number; reduced: boolean; drawn: MotionValue<number>; following: boolean }) {
  const y = useRuleY(rule, scale, height, reduced, following);
  const opacity = useTransform(drawn, [.55, 1], [0, 1]);
  return <motion.span className={styles.ruleLabel} data-tone={rule.tone ?? "neutral"} style={{ y, opacity }}>{rule.label}</motion.span>;
}

/**
 * Foundry (Analytics15) — poignée d'une règle déplaçable : un `role="slider"` posé sur la règle, hors du tracé (jamais imbriqué dans
 * le curseur du tracé). Le pointeur convertit sa hauteur en valeur par l'échelle affichée, au pas près et bornée ; le clavier suit
 * les touches d'un curseur. La valeur reste contrôlée : la poignée ne fait que rapporter.
 */
function RuleHandle({ rule, scale, height, reduced, plot, onFollow }: { rule: LineChartRule & { drag: LineChartRuleDrag }; scale: Scale; height: number; reduced: boolean; plot: RefObject<HTMLDivElement | null>; onFollow: (key: string | null) => void }) {
  const drag = rule.drag;
  const [following, setFollowing] = useState(false);
  const y = useRuleY(rule, scale, height, reduced, following);
  const latest = useRef(rule.value);
  const snap = (value: number) => clamp(clean(drag.min + Math.round((value - drag.min) / drag.step) * drag.step), drag.min, drag.max);
  const valueAt = (clientY: number) => {
    const rect = plot.current?.getBoundingClientRect();
    if (!rect?.height) return rule.value;
    const lo = scale.min.get(), span = scale.max.get() - lo || 1;
    return snap(lo + (1 - (clientY - rect.top - TOP) / (height - TOP)) * span);
  };
  const report = (value: number, commit: boolean) => {
    if (value !== latest.current) { latest.current = value; drag.onChange(value); }
    if (commit) drag.onCommit?.(value);
  };
  useEffect(() => { latest.current = rule.value; }, [rule.value]);
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (drag.disabled) return;
    const big = drag.largeStep ?? drag.step * 5;
    const next = ({ ArrowUp: rule.value + drag.step, ArrowRight: rule.value + drag.step, ArrowDown: rule.value - drag.step, ArrowLeft: rule.value - drag.step, PageUp: rule.value + big, PageDown: rule.value - big, Home: drag.min, End: drag.max } as Record<string, number>)[event.key];
    if (next === undefined) return;
    event.preventDefault();
    report(snap(next), true);
  };
  const end = (event: PointerEvent<HTMLDivElement>) => {
    if (!following) return;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    setFollowing(false); onFollow(null);
    drag.onCommit?.(latest.current);
  };
  return <motion.div className={styles.ruleHandle} data-tone={rule.tone ?? "neutral"} data-following={following || undefined} style={{ y }} role="slider" tabIndex={drag.disabled ? -1 : 0}
    aria-label={drag.label} aria-orientation="vertical" aria-valuemin={drag.min} aria-valuemax={drag.max} aria-valuenow={rule.value} aria-valuetext={drag.valueText ? drag.valueText(rule.value) : rule.label} aria-disabled={drag.disabled || undefined}
    onKeyDown={onKeyDown}
    onPointerDown={event => { if (drag.disabled) return; event.preventDefault(); event.currentTarget.setPointerCapture?.(event.pointerId); setFollowing(true); onFollow(rule.key); report(valueAt(event.clientY), false); }}
    onPointerMove={event => { if (following) report(valueAt(event.clientY), false); }}
    onPointerUp={end} onPointerCancel={end}>
    <span className={styles.ruleGrip} aria-hidden="true" />
  </motion.div>;
}

/** Axis labels that fit: at most one per 76 pixels, counted back from the latest so the newest date always shows. */
function axisPicks(data: LineChartDatum[], width: number) {
  const labeled = data.flatMap((item, index) => item.axisLabel ? [index] : []);
  if (!width || !labeled.length) return [];
  const room = Math.max(2, Math.floor(width / 76));
  const stride = Math.ceil(labeled.length / room);
  return labeled.reverse().filter((_, rank) => rank % stride === 0).reverse();
}

const NO_RULES: LineChartRule[] = [];
const NO_BANDS: LineChartBand[] = [];
const NO_MARKERS: LineChartMarker[] = [];

function LineChartReady({ data, series, label, unit = "", height = 220, formatValue, formatTick = value => compact.format(value), hiddenSeries, defaultHiddenSeries, onHiddenSeriesChange, onActiveChange, loading = false, emptyLabel = "No data for this range", legend, categoryLabel = "Date", curve = "smooth", rules = NO_RULES, stack = "none", missing = "zero", active: activeProp, domain, progress, progressLabel, progressAxisLabel, bands = NO_BANDS, markers = NO_MARKERS, interactive = true, tooltip = true, axes = true, ref, className }: LineChartProps) {
  const reduced = useReducedMotionSafe();
  const figure = useRef<HTMLElement>(null);
  const plot = useRef<HTMLDivElement>(null);
  const tip = useRef<HTMLDivElement>(null);
  const clip = useRef<SVGRectElement>(null);
  const crosshair = useRef<SVGLineElement>(null);
  const lines = useRef(new Map<string, SVGPathElement>());
  const areas = useRef(new Map<string, SVGPathElement>());
  const dots = useRef(new Map<string, SVGCircleElement>());
  const groups = useRef(new Map<string, SVGGElement>());
  // Foundry (Analytics15) : révélation progressive, tête de lecture, bandes, repères et horloge d'axe, tous peints sans rendu React.
  const revealClip = useRef<SVGRectElement>(null);
  const playhead = useRef<SVGLineElement>(null);
  const tipDot = useRef<SVGCircleElement>(null);
  const tipLabel = useRef<HTMLSpanElement>(null);
  const axisClock = useRef<HTMLSpanElement>(null);
  const axisRow = useRef<HTMLDivElement>(null);
  const bandPaths = useRef(new Map<string, SVGPathElement>());
  const markerDots = useRef(new Map<string, SVGCircleElement>());
  const ownProgress = useMotionValue(typeof progress === "number" ? progress : 1);
  const progressSource = isMotionValue(progress) ? progress : ownProgress;
  const progressive = progress !== undefined;
  useEffect(() => { if (typeof progress === "number") ownProgress.set(clamp(progress, 0, 1)); }, [ownProgress, progress]);
  const [following, setFollowing] = useState<string | null>(null);
  useImperativeHandle(ref, () => figure.current as HTMLElement);
  const inView = useInView(figure, { once: true, amount: .3 });
  const uid = `line${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const last = data.length - 1;
  const empty = data.length === 0;

  // Hidden series, controlled or not.
  const [ownHidden, setOwnHidden] = useState<string[]>(defaultHiddenSeries ?? []);
  const hidden = hiddenSeries ?? ownHidden;
  const toggle = (key: string) => {
    const next = hidden.includes(key) ? hidden.filter(item => item !== key) : [...hidden, key];
    if (hiddenSeries === undefined) setOwnHidden(next);
    onHiddenSeriesChange?.(next);
  };
  const visible = series.filter(item => !hidden.includes(item.key));
  const showLegend = legend ?? series.length > 1;

  // Each series becomes a dense curve in data units; the scale fits only the series on show.
  // Foundry (Finance8) : `missing="gap"` garde l'absence (jamais un zéro plausible) ; `stack="signed"` empile en bandes signées.
  const gaps = missing === "gap";
  const stacked = stack === "signed";
  // Sans `gap`, lecture historique inchangée (`?? 0`) ; avec `gap`, une valeur absente ou non finie est un trou.
  const read = (item: LineChartDatum, key: string): number | undefined => { const value = item.values[key]; return gaps ? (value !== undefined && Number.isFinite(value) ? value : undefined) : value ?? 0; };
  const signature = `${curve}|${stack}|${missing}|${stacked ? hidden.join(",") : ""}|${series.map(item => `${item.key}${item.overlay ? "~" : ""}`).join(",")}|${data.map(item => `${item.key}${item.x === undefined ? "" : `@${item.x}`}:${series.map(line => read(item, line.key) ?? "_").join(",")}`).join(";")}`;
  // Foundry (Analytics15 F-R2) : axe réel facultatif. La courbe reste celle calculée sur l'axe ordinal (même interpolation), seules
  // ses abscisses sont reportées aux vrais intervalles ; aucun point de mesure ajouté.
  const axis = useMemo(() => axisPositions(data), [data.map(item => item.x ?? "").join(",")]);
  const positionOf = (index: number) => axis ? itemAt(axis, clamp(index, 0, axis.length - 1)) : last > 0 ? index / last : .5;
  const stackedRaw = useMemo(() => stacked ? stackSigned(data, series, hidden, read) : null, [signature]);
  const targets = useMemo(() => {
    const place = (shape: Point[]): Point[] => axis ? shape.map(([x, value]): Point => [toAxis(axis, x), value]) : shape;
    return new Map<string, Target>(series.map(line => {
      const smooth = curve === "smooth";
      const raw = stackedRaw?.get(line.key);
      if (raw) return [line.key, { top: place(curveWithGaps(raw.top, smooth)), base: raw.base ? place(curveWithGaps(raw.base, smooth)) : null }];
      return [line.key, { top: place(curveWithGaps(data.map(item => read(item, line.key)), smooth)), base: null }];
    }));
  }, [signature, axis]);
  let low = Infinity, high = -Infinity;
  const extend = (value: number | undefined) => { if (value === undefined) return; low = Math.min(low, value); high = Math.max(high, value); };
  for (const line of visible) {
    const raw = stackedRaw?.get(line.key);
    if (raw) { raw.top.forEach(extend); raw.base?.forEach(extend); }
    else for (const item of data) extend(read(item, line.key));
  }
  // Les règles appartiennent au domaine : un budget au-dessus des données reste sur le tracé. Foundry (Analytics15) : une règle
  // déplaçable n'élargit pas le domaine (il bougerait sous le doigt) ; un domaine explicite garde ses bornes exactes.
  if (Number.isFinite(low)) for (const rule of rules) if (!rule.drag) extend(rule.value);
  const explicit = domain && Number.isFinite(domain.min) && Number.isFinite(domain.max) && domain.max > domain.min ? domain : null;
  const range = explicit ? scaleWithin(explicit.min, explicit.max) : Number.isFinite(low) ? niceScale(low, high) : null;
  const [steady, setSteady] = useState(() => range ?? niceScale(0, 100));
  if (range && (range.min !== steady.min || range.max !== steady.max)) setSteady(range);
  const scale = { min: useMotionValue(steady.min), max: useMotionValue(steady.max) };

  // Scrubbing: a new range lets go of the crosshair. Foundry (Analytics15) : avec `active`, le réticule est contrôlé ; les gestes
  // ne font que demander un index par `onActiveChange`, sans état interne.
  const controlledActive = activeProp !== undefined;
  const [ownActive, setOwnActive] = useState<number | null>(null);
  const active = controlledActive ? activeProp : ownActive;
  const [seenSignature, setSeenSignature] = useState(signature);
  if (seenSignature !== signature) { setSeenSignature(signature); if (!controlledActive) setOwnActive(null); }
  const index = active === null || empty ? null : clamp(Math.round(active), 0, last);
  const scrubbing = index !== null;
  const onActive = useRef(onActiveChange);
  useLayoutEffect(() => { onActive.current = onActiveChange; });
  const setActive = (next: number | null | ((current: number | null) => number | null)) => {
    const value = typeof next === "function" ? next(index) : next;
    if (!controlledActive) { setOwnActive(value); return; }
    if (value !== index) onActive.current?.(value, value === null ? null : data[value] ?? null);
  };

  const width = useRef(0);
  const [plotWidth, setPlotWidth] = useState(0);
  const tipSize = useRef({ w: 0, h: 0 });
  const tracks = useRef(new Map<string, Track>());
  const drawn = useMotionValue(0);
  const cursor = useMotionValue(1);
  const tipTargetX = useMotionValue(0), tipTargetY = useMotionValue(0);
  const tipSpringX = useSpring(tipTargetX, follow), tipSpringY = useSpring(tipTargetY, follow);
  const live = useRef({ series, hidden, stacked, bands, markers, last, progressLabel, progressAxisLabel, axis });
  useLayoutEffect(() => { live.current = { series, hidden, stacked, bands, markers, last, progressLabel, progressAxisLabel, axis }; });

  // One writer for every drawn attribute: morphs, toggles, the scale, the crosshair, and the measured size all repaint through here.
  const paint = useCallback(() => {
    const w = width.current;
    if (!w) return;
    const lo = scale.min.get(), span = scale.max.get() - lo || 1;
    const toY = (value: number) => TOP + (1 - (value - lo) / span) * (height - TOP);
    const at = cursor.get();
    const dotYs: number[] = [];
    for (const line of live.current.series) {
      const track = tracks.current.get(line.key);
      if (!track) continue;
      const p = track.presence;
      // Dans une pile, une série masquée est déjà repliée sur sa base par les cibles : la présence ne règle que l'opacité.
      const banded = live.current.stacked && !line.overlay;
      const y = (value: number) => banded ? toY(value) : toY(lo + (value - lo) * p);
      const coord = ([x, value]: Point) => `${(x * w).toFixed(1)},${y(value).toFixed(1)}`;
      // Une série qui ne couvre pas toute la largeur (missing="gap") ferme son aire sous ses propres bords, jamais au bord du tracé.
      const head = track.shape[0], tail = track.shape[track.shape.length - 1];
      const broken = track.base !== null || track.shape.some(point => !Number.isFinite(point[1])) || (!!head && !!tail && (head[0] > 0 || tail[0] < 1));
      if (!broken) {
        const path = track.shape.length ? `M${track.shape.map(coord).join("L")}` : "";
        lines.current.get(line.key)?.setAttribute("d", path);
        areas.current.get(line.key)?.setAttribute("d", path ? `${path}L${w},${height}L0,${height}Z` : "");
      } else {
        // Foundry (Finance8) : segments continus ; aire jusqu'au bas du tracé, ou bande entre la base et le haut d'une pile.
        const tops = segmentsOf(track.shape), bases = track.base ? segmentsOf(track.base) : null;
        lines.current.get(line.key)?.setAttribute("d", tops.map(segment => `M${segment.map(coord).join("L")}`).join(""));
        areas.current.get(line.key)?.setAttribute("d", tops.map((segment, index) => {
          const head = itemAt(segment, 0), tail = itemAt(segment, segment.length - 1), floor = bases?.[index];
          return floor && floor.length ? `M${segment.map(coord).join("L")}L${[...floor].reverse().map(coord).join("L")}Z` : `M${segment.map(coord).join("L")}L${(tail[0] * w).toFixed(1)},${height}L${(head[0] * w).toFixed(1)},${height}Z`;
        }).join(""));
      }
      const group = groups.current.get(line.key);
      if (group) group.style.opacity = String(p);
      const dot = dots.current.get(line.key);
      if (dot && track.shape.length) {
        const reading = valueAt(track.shape, at);
        const cy = Number.isFinite(reading) ? y(reading) : Number.NaN;
        dot.style.visibility = Number.isFinite(cy) ? "" : "hidden";
        dot.setAttribute("cx", (at * w).toFixed(1));
        if (Number.isFinite(cy)) dot.setAttribute("cy", cy.toFixed(1));
        if (p > .5 && Number.isFinite(cy)) dotYs.push(cy);
      }
    }
    clip.current?.setAttribute("width", String(drawn.get() * (w + 16)));
    // Foundry (Analytics15) : bandes entre deux séries (cône), là où les deux ont une valeur.
    const { bands: bandList, markers: markerList, last: lastIndex, progressLabel: tipText, progressAxisLabel: clockText, axis: realAxis } = live.current;
    // Foundry (F-R2) : index fractionnaire ↔ position, sur l'axe réel s'il existe (repères et libellés de progression cohérents).
    const indexToX = (at: number) => realAxis ? toAxis(realAxis, lastIndex > 0 ? at / lastIndex : 0) : lastIndex > 0 ? clamp(at / lastIndex, 0, 1) : .5;
    const xToIndex = (p: number) => realAxis ? axisToIndex(realAxis, p) : p * lastIndex;
    for (const band of bandList) {
      const node = bandPaths.current.get(band.key);
      const upper = tracks.current.get(band.upper), lower = tracks.current.get(band.lower);
      if (!node) continue;
      if (!upper || !lower || live.current.hidden.includes(band.upper) || live.current.hidden.includes(band.lower)) { node.setAttribute("d", ""); continue; }
      node.setAttribute("d", segmentsOf(upper.shape).map(segment => {
        const floor = segment.map(([x]): Point => [x, valueAt(lower.shape, x)]).filter(point => Number.isFinite(point[1]));
        if (floor.length !== segment.length) return "";
        const coord = ([x, value]: Point) => `${(x * w).toFixed(1)},${toY(value).toFixed(1)}`;
        return `M${segment.map(coord).join("L")}L${[...floor].reverse().map(coord).join("L")}Z`;
      }).join(""));
    }
    // Repères posés sur leur série, à une position fractionnaire de l'axe du temps.
    for (const marker of markerList) {
      const node = markerDots.current.get(marker.key), track = tracks.current.get(marker.series);
      if (!node || !track) continue;
      const x = indexToX(marker.at), reading = valueAt(track.shape, x);
      node.setAttribute("cx", (x * w).toFixed(1));
      if (Number.isFinite(reading)) node.setAttribute("cy", toY(reading).toFixed(1));
    }
    if (progressive) {
      const p = clamp(progressSource.get(), 0, 1), px = p * w;
      revealClip.current?.setAttribute("width", String(Math.min(drawn.get() * (w + 16), px + 8)));
      const head = Math.round(px) + .5;
      playhead.current?.setAttribute("x1", String(head));
      playhead.current?.setAttribute("x2", String(head));
      // La pointe : la première série révélée et visible.
      const lead = live.current.series.find(line => !line.whole && !live.current.hidden.includes(line.key));
      const leadTrack = lead ? tracks.current.get(lead.key) : undefined;
      const reading = leadTrack ? valueAt(leadTrack.shape, p) : Number.NaN;
      const tipY = Number.isFinite(reading) ? toY(reading) : Number.NaN;
      if (tipDot.current) { tipDot.current.style.visibility = Number.isFinite(tipY) ? "" : "hidden"; tipDot.current.setAttribute("cx", px.toFixed(1)); if (Number.isFinite(tipY)) tipDot.current.setAttribute("cy", tipY.toFixed(1)); }
      const label = tipLabel.current;
      if (label && tipText) {
        const text = tipText(xToIndex(p));
        if (label.textContent !== text) label.textContent = text;
        label.style.visibility = Number.isFinite(tipY) ? "" : "hidden";
        const lw = label.offsetWidth;
        label.style.transform = `translate(${clamp(px - lw / 2, 0, Math.max(0, w - lw)).toFixed(1)}px, ${(Number.isFinite(tipY) ? Math.max(0, tipY - 30) : 0).toFixed(1)}px)`;
      }
      const clock = axisClock.current;
      if (clock && clockText) {
        const text = clockText(xToIndex(p));
        if (clock.textContent !== text) clock.textContent = text;
        const cw = clock.offsetWidth, left = clamp(px - cw / 2, 0, Math.max(0, w - cw));
        clock.style.transform = `translateX(${left.toFixed(1)}px)`;
        // Les libellés d'axe s'écartent de l'horloge au lieu de la chevaucher.
        axisRow.current?.querySelectorAll<HTMLElement>("[data-axis-text]").forEach(node => {
          const box = node.getBoundingClientRect(), row = axisRow.current!.getBoundingClientRect();
          const start = box.left - row.left, end = start + box.width;
          const gap = Math.max(left - end, start - (left + cw));
          node.style.opacity = String(clamp((gap - 4) / 12, 0, 1));
        });
      }
    }
    const cx = Math.round(at * w) + .5;
    crosshair.current?.setAttribute("x1", String(cx));
    crosshair.current?.setAttribute("x2", String(cx));
    // The tooltip sits beside the crosshair, flips sides before it would leave the plot, and centres on the points it describes.
    const { w: tw, h: th } = tipSize.current;
    let x = cx + GAP;
    if (x + tw > w) x = cx - GAP - tw;
    tipTargetX.set(clamp(x, 0, Math.max(0, w - tw)));
    const middle = dotYs.length ? dotYs.reduce((sum, value) => sum + value, 0) / dotYs.length : height / 2;
    tipTargetY.set(clamp(middle - th / 2, 0, Math.max(0, height - th)));
  }, [cursor, drawn, height, progressSource, progressive, scale.max, scale.min, tipTargetX, tipTargetY]);
  useMotionValueEvent(cursor, "change", paint);
  useMotionValueEvent(progressSource, "change", paint);
  useMotionValueEvent(drawn, "change", paint);
  useMotionValueEvent(scale.min, "change", paint);
  useMotionValueEvent(scale.max, "change", paint);
  useLayoutEffect(paint);

  // The plot draws in its own pixels: measure the width, follow resizes, and size the tooltip for its flip.
  useLayoutEffect(() => {
    const node = plot.current, bubble = tip.current;
    if (!node) return;
    const read = () => { width.current = node.clientWidth; setPlotWidth(node.clientWidth); if (bubble) tipSize.current = { w: bubble.offsetWidth, h: bubble.offsetHeight }; paint(); };
    read();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(read);
    observer.observe(node);
    if (bubble) observer.observe(bubble);
    return () => observer.disconnect();
  }, [paint]);

  // New data morphs every line from the shape on screen, sampled on shared x positions so a range that gains or loses points still flows.
  const morph = useRef<{ stop: () => void } | null>(null);
  useEffect(() => {
    type Lane = { from: number[]; to: number[] };
    const moves: { track: Track; xs: number[]; top: Lane; base: Lane | null; target: Target }[] = [];
    for (const [key, target] of targets) {
      const track = tracks.current.get(key);
      if (!track) { tracks.current.set(key, { shape: target.top, base: target.base, presence: live.current.hidden.includes(key) ? 0 : 1, fade: null }); continue; }
      if (!track.shape.length || !target.top.length || reduced) { update(track, { shape: target.top, base: target.base }); continue; }
      // Foundry (Finance8) : une forme à trous ne morphe que sur l'étendue de sa cible, pour ne jamais tracer hors de ses données.
      const finite = target.top.filter(point => Number.isFinite(point[1]));
      const [x0, x1] = finite.length ? [itemAt(finite, 0)[0], itemAt(finite, finite.length - 1)[0]] : [0, 1];
      const xs = [...new Set([...grid.filter(x => x >= x0 && x <= x1), ...target.top.map(([x]) => x)])].sort((a, b) => a - b);
      const lane = (from: Point[], to: Point[]): Lane => ({ from: xs.map(x => valueAt(from, x)), to: xs.map(x => valueAt(to, x)) });
      moves.push({ track, xs, top: lane(track.shape, target.top), base: target.base ? lane(track.base ?? target.base, target.base) : null, target });
    }
    morph.current?.stop();
    if (!moves.length) { paint(); return; }
    const mix = (move: { xs: number[] }, lane: Lane, progress: number) => move.xs.map((x, at): Point => { const from = itemAt(lane.from, at); return [x, from + (itemAt(lane.to, at) - from) * progress]; });
    morph.current = animate(0, 1, { ...settle, restDelta: .0005, onUpdate: progress => { for (const move of moves) update(move.track, { shape: mix(move, move.top, progress), base: move.base ? mix(move, move.base, progress) : null }); paint(); }, onComplete: () => { for (const move of moves) update(move.track, { shape: move.target.top, base: move.target.base }); paint(); } });
    return () => morph.current?.stop();
  }, [paint, reduced, targets]);

  // A hidden series flattens into the baseline as it fades; the ones that stay rescale around it.
  const hiddenKey = hidden.join("|");
  useEffect(() => {
    for (const [key, track] of tracks.current) {
      const to = hidden.includes(key) ? 0 : 1;
      if (track.presence === to) continue;
      track.fade?.stop();
      if (reduced) { update(track, { presence: to }); continue; }
      update(track, { fade: animate(track.presence, to, { ...settle, onUpdate: value => { update(track, { presence: value }); paint(); } }) });
    }
    paint();
  }, [hiddenKey, paint, reduced]);

  useEffect(() => {
    if (reduced) { scale.min.jump(steady.min); scale.max.jump(steady.max); return; }
    const controls = [animate(scale.min, steady.min, settle), animate(scale.max, steady.max, settle)];
    return () => controls.forEach(control => control.stop());
  }, [reduced, scale.max, scale.min, steady]);

  // The lines draw left to right the first time the chart is seen with data.
  useEffect(() => {
    if (empty) return;
    if (reduced) { drawn.jump(1); return; }
    if (!inView || drawn.get() >= 1) return;
    const controls = animate(drawn, 1, draw);
    return () => controls.stop();
  }, [drawn, empty, inView, reduced]);

  // The crosshair appears on the first scrubbed point and glides between the ones after it; the tooltip follows on its own spring.
  const wasScrubbing = useRef(false);
  useEffect(() => {
    if (index === null) { wasScrubbing.current = false; return; }
    const to = positionOf(index);
    if (reduced || !wasScrubbing.current) { cursor.jump(to); tipSpringX.jump(tipTargetX.get()); tipSpringY.jump(tipTargetY.get()); }
    wasScrubbing.current = true;
    if (reduced) return;
    const controls = animate(cursor, to, glide);
    return () => controls.stop();
  }, [axis, cursor, index, last, reduced, tipSpringX, tipSpringY, tipTargetX, tipTargetY]);

  useEffect(() => { if (!controlledActive) onActive.current?.(index, index === null ? null : data[index] ?? null); }, [index]);

  const pointAt = (clientX: number) => {
    const rect = plot.current?.getBoundingClientRect();
    if (!rect?.width || last < 1) return Math.max(0, last);
    const share = (clientX - rect.left) / rect.width;
    // Axe réel : le point le plus proche en position, pas en rang.
    if (axis) return axis.reduce((best, position, at) => Math.abs(position - share) < Math.abs(itemAt(axis, best) - share) ? at : best, 0);
    return clamp(Math.round(share * last), 0, last);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape" && scrubbing) { event.preventDefault(); setActive(null); return; }
    const from = index ?? last + 1, page = Math.max(1, Math.round(data.length / 6));
    const next = ({ ArrowLeft: from - 1, ArrowDown: from - 1, ArrowRight: index === null ? last : from + 1, ArrowUp: index === null ? last : from + 1, PageDown: from - page, PageUp: from + page, Home: 0, End: last } as Record<string, number>)[event.key];
    if (next === undefined || empty) return;
    event.preventDefault();
    setActive(clamp(next, 0, last));
  };
  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (empty) return;
    if (event.pointerType !== "mouse") event.currentTarget.setPointerCapture?.(event.pointerId);
    setActive(pointAt(event.clientX));
  };
  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => { if (!empty && (event.pointerType === "mouse" || event.buttons)) setActive(pointAt(event.clientX)); };

  const format = (value: number, line: LineChartSeries) => `${formatValue ? formatValue(value, line) : grouped.format(value)}${unit ? ` ${unit}` : ""}`;
  // Foundry (Finance8) : la lecture d'une série est toujours sa PROPRE valeur (jamais le cumul d'une pile) ; un trou se dit.
  const NO_DATA = "Aucune donnée";
  const say = (item: LineChartDatum, line: LineChartSeries) => { const value = read(item, line.key); return value === undefined ? NO_DATA : format(value, line); };
  const reading = index === null ? null : data[index];
  const valueText = reading ? `${reading.label}: ${visible.map(line => `${line.label} ${say(reading, line)}`).join(", ")}` : empty ? emptyLabel : `${visible.length} séries affichées sur ${series.length}`;
  // Premier et dernier datum existent exactement quand les données ne sont pas vides.
  const firstDatum = data[0], lastDatum = data[last];
  const ruleText = rules.length ? ` ${rules.map(rule => rule.label).join(". ")}.` : "";
  const summary = !firstDatum || !lastDatum ? `${label}. ${emptyLabel}.` : `${label}, ${firstDatum.label} à ${lastDatum.label}. ${visible.map(line => `${line.label}, dernière valeur ${say(lastDatum, line)}`).join(". ")}.${ruleText}`;
  const picks = axisPicks(data, plotWidth);
  const allHidden = !empty && visible.length === 0;
  const tipX = reduced ? tipTargetX : tipSpringX, tipY = reduced ? tipTargetY : tipSpringY;

  return <figure ref={figure} className={[styles.figure, className].filter(Boolean).join(" ")} aria-label={label} aria-busy={loading || undefined}>
    {showLegend && <div className={styles.legend} role="group" aria-label={`${label}, séries`}>
      {series.map((line, at) => <button key={line.key} type="button" className={styles.toggle} aria-pressed={!hidden.includes(line.key)} onClick={() => toggle(line.key)} style={{ "--series": colorOf(line, at) } as CSSProperties}>
        <span className={styles.swatch} data-dashed={line.dashed || undefined} aria-hidden="true" />
        <span className={styles.toggleLabel}>{line.label}</span>
      </button>)}
    </div>}
    <div className={styles.chart} data-scrubbing={scrubbing || undefined} data-loading={loading || undefined} data-progressive={progressive || undefined} data-bare={!axes || undefined}>
      <div ref={plot} className={styles.plot} style={{ height }} data-interactive={interactive || undefined} {...(interactive ? {
        role: empty ? "group" : "slider", tabIndex: empty ? -1 : 0, "aria-label": `${label}, explorer par ${categoryLabel.toLowerCase()}`, "aria-orientation": "horizontal" as const, "aria-valuemin": empty ? undefined : 1, "aria-valuemax": empty ? undefined : data.length, "aria-valuenow": empty ? undefined : (index ?? last) + 1, "aria-valuetext": valueText,
        onPointerDown, onPointerMove, onPointerUp: (event: PointerEvent<HTMLDivElement>) => { if (event.pointerType !== "mouse") setActive(null); }, onPointerCancel: () => setActive(null), onPointerLeave: (event: PointerEvent<HTMLDivElement>) => { if (event.pointerType === "mouse") setActive(null); }, onKeyDown, onBlur: () => setActive(null),
        onFocus: (event: FocusEvent<HTMLDivElement>) => { if (event.currentTarget.matches(":focus-visible") && !empty) setActive(current => current ?? last); },
      } : {})}>
        <svg className={styles.svg} width="100%" height={height} aria-hidden="true" focusable="false">
          <defs>
            <clipPath id={`${uid}-draw`} clipPathUnits="userSpaceOnUse"><rect ref={clip} x={-8} y={-16} width={0} height={height + 32} /></clipPath>
            {progressive && <clipPath id={`${uid}-reveal`} clipPathUnits="userSpaceOnUse"><rect ref={revealClip} x={-8} y={-16} width={0} height={height + 32} /></clipPath>}
          </defs>
          <AnimatePresence initial={false}>{axes && steady.ticks.filter(value => value !== steady.min).map(value => <Gridline key={value} value={value} scale={scale} height={height} zero={value === 0 && steady.min < 0} />)}</AnimatePresence>
          {axes && <line className={styles.baseline} x1={0} x2="100%" y1={height - .5} y2={height - .5} />}
          <line ref={crosshair} className={styles.crosshair} y1={TOP - 4} y2={height} />
          {bands.length > 0 && <g className={styles.series} clipPath={`url(#${uid}-draw)`}>{bands.map(band => <path key={band.key} ref={node => { if (node) bandPaths.current.set(band.key, node); else bandPaths.current.delete(band.key); }} className={styles.band} data-tone={band.tone ?? "accent"} />)}</g>}
          <g className={styles.series} clipPath={`url(#${uid}-draw)`}>
            {series.map((line, at) => <g key={line.key} ref={node => { if (node) groups.current.set(line.key, node); else groups.current.delete(line.key); }} clipPath={progressive && !line.whole ? `url(#${uid}-reveal)` : undefined} style={{ "--series": colorOf(line, at) } as CSSProperties}>
              {(line.area ?? (stacked ? !line.overlay : at === 0)) && <path ref={node => { if (node) areas.current.set(line.key, node); else areas.current.delete(line.key); }} className={styles.area} />}
              <path ref={node => { if (node) lines.current.set(line.key, node); else lines.current.delete(line.key); }} className={styles.line} data-dashed={line.dashed || undefined} />
            </g>)}
          </g>
          <AnimatePresence initial={false}>{rules.map(rule => <RuleLine key={rule.key} rule={rule} scale={scale} height={height} reduced={reduced} drawn={drawn} following={following === rule.key} />)}</AnimatePresence>
          {progressive && <line ref={playhead} className={styles.playhead} y1={TOP - 4} y2={height} />}
          {markers.map(marker => <circle key={marker.key} ref={node => { if (node) markerDots.current.set(marker.key, node); else markerDots.current.delete(marker.key); }} className={styles.marker} r={4.5} data-tone={marker.tone ?? "neutral"} data-shown={marker.shown || undefined} />)}
          {progressive && <circle ref={tipDot} className={styles.tipDot} r={4.5} style={{ "--series": colorOf(series.find(line => !line.whole) ?? series[0] ?? { key: "", label: "" }, Math.max(0, series.findIndex(line => !line.whole))) } as CSSProperties} />}
          {series.map((line, at) => <circle key={line.key} ref={node => { if (node) dots.current.set(line.key, node); else dots.current.delete(line.key); }} className={styles.dot} r={4} data-hidden={hidden.includes(line.key) || undefined} style={{ "--series": colorOf(line, at) } as CSSProperties} />)}
        </svg>
        {rules.length > 0 && <div className={styles.ruleLabels} aria-hidden="true">{rules.map(rule => <RuleLabel key={rule.key} rule={rule} scale={scale} height={height} reduced={reduced} drawn={drawn} following={following === rule.key} />)}</div>}
        {progressive && progressLabel && <span ref={tipLabel} className={styles.progressLabel} aria-hidden="true" />}
        {tooltip && <motion.div ref={tip} className={styles.tooltip} style={{ x: tipX, y: tipY }} aria-hidden="true">
          <p className={styles.tipTitle}>{(reading ?? data[last])?.label ?? ""}</p>
          {visible.map(line => { const shown = reading ?? data[last]; if (gaps && shown && read(shown, line.key) === undefined) return null; return <p key={line.key} className={styles.tipRow} style={{ "--series": colorOf(line, series.indexOf(line)) } as CSSProperties}>
            <span className={styles.tipSwatch} data-dashed={line.dashed || undefined} />
            <span className={styles.tipName}>{line.label}</span>
            <span className={styles.tipValue}>{gaps ? (shown ? say(shown, line) : "") : format(shown?.values[line.key] ?? 0, line)}</span>
          </p>; })}
        </motion.div>}
        <AnimatePresence initial={false}>
          {(empty || allHidden) && !loading && <motion.p key={empty ? "empty" : "hidden"} className={styles.message} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, transition: fadeFast }}>{empty ? emptyLabel : "Choisissez une série à afficher"}</motion.p>}
          {loading && empty && <motion.span key="skeleton" className={styles.skeleton} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, transition: fadeFast }} aria-hidden="true" />}
        </AnimatePresence>
      </div>
      {axes && <div className={styles.gutter} aria-hidden="true">
        <AnimatePresence initial={false}>{steady.ticks.map(value => <TickLabel key={value} value={value} scale={scale} height={height} format={formatTick} />)}</AnimatePresence>
      </div>}
      {axes && <div ref={axisRow} className={styles.axis} aria-hidden="true">
        <AnimatePresence initial={false}>
          {picks.map(at => { const datum = data[at]; if (!datum) return null; const share = positionOf(at); return <motion.span key={`${datum.key}:${datum.axisLabel}`} className={styles.axisLabel} style={{ left: `${share * 100}%`, translateX: `${-share * 100}%` }} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, transition: fadeFast }} transition={{ duration: duration.standard, ease: [...ease.standard] }}><span data-axis-text="">{datum.axisLabel}</span></motion.span>; })}
        </AnimatePresence>
        {progressive && progressAxisLabel && <span ref={axisClock} className={styles.axisClock} />}
      </div>}
      {/* Foundry (Analytics15) : poignées des règles déplaçables, posées sur le tracé mais jamais imbriquées dans son curseur. */}
      {rules.some(rule => rule.drag) && <div className={styles.handles} style={{ height }}>
        {rules.map(rule => rule.drag ? <RuleHandle key={rule.key} rule={rule as LineChartRule & { drag: LineChartRuleDrag }} scale={scale} height={height} reduced={reduced} plot={plot} onFollow={setFollowing} /> : null)}
      </div>}
    </div>
    <p className={styles.srOnly}>{summary}</p>
    {/* Foundry (Finance8) : une table ignore `width: 1px` ; masquée dans un bloc, elle ne fait plus déborder la page étroite. */}
    {!empty && <div className={styles.srOnly}><table>
      <caption>{label}</caption>
      <thead><tr><th scope="col">{categoryLabel}</th>{series.map(line => <th key={line.key} scope="col">{line.label}</th>)}</tr></thead>
      <tbody>{data.map(item => <tr key={item.key}><th scope="row">{item.label}</th>{series.map(line => <td key={line.key}>{say(item, line)}</td>)}</tr>)}</tbody>
    </table></div>}
    <p className={styles.srOnly} aria-live="polite" aria-atomic="true">{loading ? "Chargement" : ""}</p>
  </figure>;
}

export default LineChart;

export function LineChart(props: LineChartProps) {
  // In gap mode the source deliberately treats non-finite samples as missing.
  if (props.missing !== "gap" && props.data.some(datum => props.series.some(series => datum.values[series.key] !== undefined && !Number.isFinite(datum.values[series.key])))) return <span role="status">{props.label} : Donnée indisponible</span>;
  return <LineChartReady {...props} />;
}
